import type { VolunteerTeamAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'
import UserVolunteerRoleQ from './UserVolunteerRoleQ'
import VolunteerRoleQ from './VolunteerRoleQ'

const VolunteerTeamQ = {
  ...makeQueryModelV2<VolunteerTeamAttributes>('volunteer_team')([
    'id',
    'slug',
    'name',
    'icon_url',
    'description',
    'resources',
    'archived_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    VolunteerRoles: {
      query: VolunteerRoleQ,
      foreignKey: VolunteerRoleQ.volunteer_team_id,
      sourceKey: VolunteerTeamQ.id,
    },
  }),

  where: (
    {
      id,
      slug = null,
      isArchived = null,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      slug?: string | null | SQLFragment<string>
      isArchived?: boolean | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<VolunteerTeamAttributes> => SQL`
        FROM ${VolunteerTeamQ}
        ${includeAssociationsToSql(VolunteerTeamQ, include)}
        WHERE
            ${id != null ? SQL`${VolunteerTeamQ.id} = ${id} AND` : SQL``}
            ${slug != null ? SQL`${VolunteerTeamQ.slug} = ${slug} AND` : SQL``}
            ${
              isArchived === true
                ? SQL`${VolunteerTeamQ.archived_at} IS NOT NULL AND`
                : isArchived === false
                  ? SQL`${VolunteerTeamQ.archived_at} IS NULL AND`
                  : SQL``
            }
            ${paranoid ? SQL`${VolunteerTeamQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getCurrentTeamMembers: () => SQL`
      ${VolunteerRoleQ.where({
        volunteerTeamId: VolunteerTeamQ.id,
        include: [
          {
            association: 'UserVolunteerRoles',
            required: true,
            where: SQL`${UserVolunteerRoleQ.isActive()}`,
            include: [{ association: 'User', required: true }],
          },
        ],
      })}`,

  hasOpenRole: (): SQLFragment<boolean> => SQL`
      EXISTS (
          SELECT 1
          ${VolunteerRoleQ.where(
            { volunteerTeamId: VolunteerTeamQ.id },
            VolunteerRoleQ.isApplicationOpen()
          )}
      )`,

  isUserOnTeam: (userId: number | SQLFragment<number>): SQLFragment<boolean> => SQL`
    EXISTS (
      SELECT 1
      ${VolunteerRoleQ.where({
        volunteerTeamId: VolunteerTeamQ.id,
        include: [
          {
            association: 'UserVolunteerRoles',
            required: true,
            where: SQL`${UserVolunteerRoleQ.isActive()}`,
            include: [{ association: 'User', required: true, where: SQL`${UserQ.id} = ${userId}` }],
          },
        ],
      })}
    )
  `,
}

export default VolunteerTeamQ
